import { useState } from 'react';

import type { ConnectingDelayJudgementMode, ConnectingDirectionEntry } from '../../../api/types';
import type { RouteMaster } from '../../../data/routes';
import type { RouteStop } from '../../../data/routeStops';
import { createDefaultConnectingStationDirection } from '../../../utils/broadcastContents';
import { ConnectingDirectionBlock } from './ConnectingDirectionBlock';

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 接続線ブロック内の「方面リスト」セクション。
 *
 * 方面は 1〜2 件に制限し、＋ボタン (最大2件)・−ボタン (最後の1件は残す)・↑↓ ボタン
 * (順序変更) を提供する。方面ごとの詳細入力は `ConnectingDirectionBlock` に委譲する。
 */
export function ConnectingDirectionsSection({
  lineIndex,
  routeId,
  judgementMode,
  routes,
  routeStops,
  directions,
  onChangeDirections,
}: {
  /** 親接続線ブロックの index (data-testid 用) */
  lineIndex: number;
  /** 親接続線の route_id (方面の direction_id / 区間駅の選択肢に使う) */
  routeId: string;
  /** 親接続線の判定モード (項目表示切替に使う) */
  judgementMode: ConnectingDelayJudgementMode;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  directions: ConnectingDirectionEntry[];
  onChangeDirections: (next: ConnectingDirectionEntry[]) => void;
}): React.JSX.Element {
  // タブ表示: 選択中の方面 index。方面が 2 件あるとき横並びタブで切り替える。
  const [activeDir, setActiveDir] = useState<number>(0);
  const activeDirIndex = Math.min(activeDir, Math.max(0, directions.length - 1));

  /** index 番目の方面を更新 */
  const updateDirection = (index: number, next: ConnectingDirectionEntry): void => {
    const nextDirections = directions.slice();
    nextDirections[index] = next;
    onChangeDirections(nextDirections);
  };
  /**
   * 方面を末尾に追加 (最大 2 件まで)。
   *
   * 方面はタブ表示で選択中の 1 件ぶんしか本文を描画しないため、追加直後に
   * 選択タブを新しく追加した方面 (= 追加後の末尾 index) へ切り替える。
   * こうしないと「追加したのに画面が変わらない」状態になり、ユーザが
   * 追加した方面の入力欄にたどり着けない (Issue #356)。
   * 追加後の末尾 index は「追加前の件数」= `directions.length` となる。
   */
  const addDirection = (): void => {
    if (directions.length >= 2) return;
    setActiveDir(directions.length);
    onChangeDirections([...directions, createDefaultConnectingStationDirection()]);
  };
  /** 方面を削除 (最後の 1 件は残す) */
  const removeDirection = (index: number): void => {
    if (directions.length <= 1) return;
    const nextDirections = directions.slice();
    nextDirections.splice(index, 1);
    onChangeDirections(nextDirections);
  };
  /** 方面を上へ移動 */
  const moveUp = (index: number): void => {
    if (index <= 0) return;
    const nextDirections = directions.slice();
    const [moved] = nextDirections.splice(index, 1);
    if (!moved) return;
    nextDirections.splice(index - 1, 0, moved);
    onChangeDirections(nextDirections);
  };
  /** 方面を下へ移動 */
  const moveDown = (index: number): void => {
    if (index >= directions.length - 1) return;
    const nextDirections = directions.slice();
    const [moved] = nextDirections.splice(index, 1);
    if (!moved) return;
    nextDirections.splice(index + 1, 0, moved);
    onChangeDirections(nextDirections);
  };

  const canAdd = directions.length < 2;
  const canRemove = directions.length > 1;

  return (
    <div
      className="connecting-directions-section"
      data-testid={`connecting-directions-section-${lineIndex}`}
      style={{ marginTop: 8 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <strong>方面 ({directions.length}/2 件)</strong>
        {canAdd ? (
          <button
            type="button"
            data-testid={`connecting-direction-add-${lineIndex}`}
            aria-label={`接続線#${lineIndex + 1} に方面を追加`}
            onClick={addDirection}
          >
            ＋ 方面を追加
          </button>
        ) : (
          <span className="hint">最大 2 件まで追加できます</span>
        )}
      </div>
      {/* 方面タブ (横並び)。選択中の方面のみ本文を表示する。 */}
      {directions.length > 0 ? (
        <div
          className="connecting-direction-tabs"
          role="tablist"
          data-testid={`connecting-direction-tabs-${lineIndex}`}
        >
          {directions.map((direction, di) => {
            const isActive = di === activeDirIndex;
            const label = (direction.direction_name ?? '').trim() || `方面#${di + 1}`;
            return (
              <button
                key={di}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`connecting-direction-tab-${lineIndex}-${di}`}
                className={`connecting-direction-tab${isActive ? ' is-active' : ''}`}
                onClick={() => setActiveDir(di)}
              >
                #{di + 1} {label}
              </button>
            );
          })}
        </div>
      ) : null}
      <ol
        className="connecting-directions-list"
        style={{ listStyle: 'none', padding: 0, margin: 0 }}
      >
        {directions.map((direction, di) =>
          di === activeDirIndex ? (
            <li
              key={di}
              className="connecting-direction-item"
              data-testid={`connecting-direction-item-${lineIndex}-${di}`}
              style={{
                border: '1px dashed #ccc',
                borderRadius: 4,
                padding: '6px 10px',
                marginTop: 6,
              }}
            >
              <ConnectingDirectionBlock
                lineIndex={lineIndex}
                directionIndex={di}
                direction={direction}
                routeId={routeId}
                judgementMode={judgementMode}
                routes={routes}
                routeStops={routeStops}
                canRemove={canRemove}
                isFirst={di === 0}
                isLast={di === directions.length - 1}
                onChange={(next) => updateDirection(di, next)}
                onRemove={() => {
                  removeDirection(di);
                  setActiveDir(Math.max(0, di - 1));
                }}
                onMoveUp={() => {
                  moveUp(di);
                  setActiveDir(Math.max(0, di - 1));
                }}
                onMoveDown={() => {
                  moveDown(di);
                  setActiveDir(Math.min(directions.length - 1, di + 1));
                }}
              />
            </li>
          ) : null,
        )}
      </ol>
    </div>
  );
}
